import type { Ctx } from '@milkdown/kit/ctx';
import type { useNodeViewFactory } from '@prosemirror-adapter/react';
import { useNodeViewContext } from '@prosemirror-adapter/react';
import { prosePluginsCtx } from '@milkdown/kit/core';
import { Plugin } from '@milkdown/kit/prose/state';

import { VideoViewer } from './components/VideoViewer';
import { VIDEO_NODE_NAME } from './video';

/**
 * React component for rendering video blocks in the editor.
 * Uses prosemirror-adapter to bridge ProseMirror and React.
 */
export const VideoNodeView = () => {
  const { node, setAttrs, selected, view } = useNodeViewContext();

  const src = node.attrs.src as string;
  const title = (node.attrs.title as string) || '';
  const width = (node.attrs.width as number) || 640;
  const height = (node.attrs.height as number) || 360;
  const readonly = !view.editable;

  if (!src) {
    return <div className="video-error text-red-500 p-4">Invalid video URL</div>;
  }

  const handleChangeSize = (newWidth: number, newHeight: number) => {
    if (!readonly) {
      setAttrs({ width: newWidth, height: newHeight });
    }
  };

  const handleChangeTitle = (newTitle: string) => {
    if (!readonly) {
      setAttrs({ title: newTitle });
    }
  };

  return (
    <VideoViewer
      src={src}
      title={title}
      width={width}
      height={height}
      isSelected={selected}
      readonly={readonly}
      onChangeSize={handleChangeSize}
      onChangeTitle={handleChangeTitle}
    />
  );
};

/**
 * Configure the video NodeView with the editor.
 * Must be called during editor setup.
 */
export function configureVideoNodeView(ctx: Ctx, nodeViewFactory: ReturnType<typeof useNodeViewFactory>) {
  ctx.update(prosePluginsCtx, (plugins) => [
    ...plugins,
    new Plugin({
      props: {
        nodeViews: {
          [VIDEO_NODE_NAME]: nodeViewFactory({ component: VideoNodeView }),
        },
      },
    }),
  ]);
}
